import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Link } from 'react-router-dom';
import { Menu, X, User, Gift } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import Wordmark from '../brand/Wordmark';
import PartnerCodeModal from '../partner/PartnerCodeModal';

const nav = [
  { to: '/competitions', label: 'Competitions' },
  { to: '/how-it-works', label: 'How it works' },
  { to: '/winners', label: 'Winners' },
  { to: '/about', label: 'About' },
  { to: '/faq', label: 'FAQ' },
];

// Framer motion configs — stable references
const OVERLAY_INITIAL = { opacity: 0 };
const OVERLAY_ANIMATE = { opacity: 1 };
const OVERLAY_EXIT = { opacity: 0 };
const OVERLAY_TRANSITION = { duration: 0.2 };

const LINK_INITIAL = { opacity: 0, y: 8 };
const LINK_ANIMATE = { opacity: 1, y: 0 };

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const userToken = typeof window !== 'undefined' ? localStorage.getItem('pd_user_token') : null;

  // Scroll lock + Escape-to-close while overlay is open. Cleanup on unmount
  // and on close so we can never leave the body in a locked state.
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[color:var(--pd-bg)]/85 border-b border-pd" data-testid="site-header">
      {/* Main nav */}
      <div className="container-pd flex items-center justify-between h-16">
        <Wordmark />
        <nav className="hidden md:flex items-center gap-7" data-testid="primary-nav">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${isActive ? 'text-[color:var(--pd-accent-yellow)]' : 'text-muted-sage hover:text-[color:var(--pd-text)]'}`
              }
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setCodeModalOpen(true)}
            className="hidden md:inline-flex items-center gap-1.5 text-sm text-muted-sage hover:text-[color:var(--pd-text)]"
            data-testid="header-partner-code-button"
          >
            <Gift size={15} />
            Got a code?
          </button>
          <Link
            to={userToken ? '/account' : '/login'}
            className={`hidden md:inline-flex items-center gap-2 text-sm ${userToken ? 'text-[color:var(--pd-text)] hover:text-[color:var(--pd-accent-yellow)]' : 'text-muted-sage hover:text-[color:var(--pd-text)]'}`}
            data-testid="header-account-link"
          >
            <User size={16} />
            {userToken ? 'Account' : 'Sign in'}
          </Link>
          <Link
            to="/competitions"
            className="hidden md:inline-flex items-center bg-[color:var(--pd-accent-yellow)] text-[#14251c] px-4 py-2 rounded-xl font-medium hover:brightness-95"
            data-testid="header-enter-now-button"
          >
            Enter now
          </Link>
          <button
            className="md:hidden p-2 rounded-lg border border-pd relative z-[60]"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={18} className={open ? 'text-[color:var(--pd-bg)]' : ''} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Full-screen mobile overlay — rendered via portal to escape the header's
          backdrop-filter containing block (otherwise `fixed inset-0` would be
          scoped to the header rather than the viewport). */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              initial={OVERLAY_INITIAL}
              animate={OVERLAY_ANIMATE}
              exit={OVERLAY_EXIT}
              transition={OVERLAY_TRANSITION}
              className="md:hidden fixed inset-0 z-[100] nav-overlay flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
            data-testid="mobile-nav"
          >
            {/* Top bar inside overlay — mirrors header height for a stable feel */}
            <div className="h-16 flex items-center justify-between px-4 sm:px-6 border-b nav-overlay-divider" style={{ borderBottomWidth: '1px' }}>
              <span className="font-display text-[color:var(--pd-bg)] text-xl tracking-tight">The Padel Draw</span>
              <button
                className="p-2 rounded-lg border"
                style={{ borderColor: 'rgba(20,37,28,0.25)', color: 'var(--pd-bg)' }}
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Nav links — large tap targets, staggered fade-in */}
            <nav className="flex-1 overflow-y-auto px-6 pt-6 pb-8 flex flex-col gap-1" data-testid="mobile-nav-links">
              {nav.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={LINK_INITIAL}
                  animate={LINK_ANIMATE}
                  transition={{ duration: 0.25, delay: 0.05 + i * 0.04 }}
                >
                  <NavLink
                    to={n.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block font-display text-3xl leading-tight py-3 nav-overlay-link ${isActive ? 'active' : ''}`
                    }
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {n.label}
                  </NavLink>
                </motion.div>
              ))}

              <div className="h-px my-4 nav-overlay-divider" aria-hidden="true" />

              <motion.div
                initial={LINK_INITIAL}
                animate={LINK_ANIMATE}
                transition={{ duration: 0.25, delay: 0.05 + nav.length * 0.04 }}
              >
                <Link
                  to={userToken ? '/account' : '/login'}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 font-mono text-sm uppercase tracking-[0.18em] py-3 nav-overlay-link"
                  data-testid="mobile-account-link"
                >
                  <User size={16} />
                  {userToken ? 'Account' : 'Sign in'}
                </Link>
              </motion.div>

              <motion.div
                initial={LINK_INITIAL}
                animate={LINK_ANIMATE}
                transition={{ duration: 0.25, delay: 0.05 + (nav.length + 1) * 0.04 }}
              >
                <button
                  type="button"
                  onClick={() => { setOpen(false); setCodeModalOpen(true); }}
                  className="w-full flex items-center gap-2 font-mono text-sm uppercase tracking-[0.18em] py-3 nav-overlay-link text-left"
                  data-testid="mobile-partner-code-link"
                >
                  <Gift size={16} />
                  Got a code?
                </button>
              </motion.div>
            </nav>

            {/* Bottom CTA — pinned above safe area */}
            <div className="px-6 pt-4 pb-8 border-t nav-overlay-divider" style={{ borderTopWidth: '1px' }}>
              <Link
                to="/competitions"
                onClick={() => setOpen(false)}
                className="w-full inline-flex items-center justify-center bg-[color:var(--pd-bg)] text-[color:var(--pd-text)] px-5 py-4 rounded-xl font-medium text-base hover:bg-[color:var(--pd-surface)] transition-colors"
                data-testid="mobile-enter-now-button"
              >
                Enter now
              </Link>
              <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: 'rgba(20,37,28,0.6)' }}>
                Equal Odds &middot; Equal Opportunity
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>,
        document.body,
      )}

      <PartnerCodeModal open={codeModalOpen} onClose={() => setCodeModalOpen(false)} />
    </header>
  );
};

export default Header;
